import { Injectable, OnDestroy } from '@angular/core';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth/auth.service';

@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private hubConnections = new Map<string, HubConnection>();
  private readonly destroy$ = new Subject<void>();

  private readonly connectionStateSubject = new BehaviorSubject<{
    hub: string;
    state: HubConnectionState;
  }>({
    hub: '',
    state: HubConnectionState.Disconnected,
  });

  public readonly connectionState$ = this.connectionStateSubject.asObservable();

  private hubStartPromises = new Map<string, Promise<void>>();
  private hubConsumers = new Map<string, Set<string>>();
  private disconnectCallbacks = new Map<string, Function[]>();
  private reconnectCallbacks = new Map<string, Function[]>();

  constructor(private authService: AuthService) {
    this.authService.onLogout$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.disconnectAll(true);
      });
  }

  /**
   * Establishes a connection to the specified hub or returns the existing connection.
   * Enhanced to handle WebSocket errors and retry logic.
   * @param hubName The name of the hub to connect to (e.g., 'notification-hub').
   * @param url The connection URL.
   * @param queryParams Query parameters to be added to the connection URL.
   * @param consumerId The unique identifier for the consumer.
   */
  public async connect(
    hubName: string,
    url: string,
    queryParams?: URLSearchParams,
    consumerId: string = 'default'
  ): Promise<void> {
    const existing = this.hubConnections.get(hubName);
    if (existing) {
      if (existing.state === HubConnectionState.Connected) {
        let consumers = this.hubConsumers.get(hubName);
        if (!consumers) {
          consumers = new Set<string>();
          this.hubConsumers.set(hubName, consumers);
        }
        if (consumers.has(consumerId)) {
          this.connectionStateSubject.next({
            hub: hubName,
            state: HubConnectionState.Connected,
          });
          return;
        }
        consumers.add(consumerId);

        this.connectionStateSubject.next({
          hub: hubName,
          state: HubConnectionState.Connected,
        });
        return;
      }

      let consumers = this.hubConsumers.get(hubName);
      if (!consumers) {
        consumers = new Set<string>();
        this.hubConsumers.set(hubName, consumers);
      }
      if (!consumers.has(consumerId)) {
        consumers.add(consumerId);
      }
      if (this.hubStartPromises.has(hubName)) {
        return this.hubStartPromises.get(hubName);
      }
      const startPromise = existing
        .start()
        .then(() => {
          this.connectionStateSubject.next({
            hub: hubName,
            state: HubConnectionState.Connected,
          });
        })
        .catch(async (err) => {
          console.error(`[SignalR] '${hubName}' start failed:`, err);
          try {
            await this.authService.getValidAccessToken();
          } catch {}
          this.connectionStateSubject.next({
            hub: hubName,
            state: HubConnectionState.Disconnected,
          });
        })
        .finally(() => this.hubStartPromises.delete(hubName));
      this.hubStartPromises.set(hubName, startPromise);
      return startPromise;
    }

    let connectionUrl = `${url}/${hubName}`;
    if (queryParams && queryParams.toString()) {
      connectionUrl += `?${queryParams.toString()}`;
    }

    const hubConnection = new HubConnectionBuilder()
      .withUrl(connectionUrl, {
        accessTokenFactory: async () => {
          return await this.authService.getValidAccessToken();
        },
        transport: HttpTransportType.WebSockets,
        logger: environment.production
          ? LogLevel.Critical
          : LogLevel.Information,
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .build();

    this.hubConnections.set(hubName, hubConnection);
    this.hubConsumers.set(hubName, new Set([consumerId]));

    hubConnection.onclose((error) => {
      this.connectionStateSubject.next({
        hub: hubName,
        state: HubConnectionState.Disconnected,
      });
      console.warn(`SignalR connection to '${hubName}' closed.`, error);
      const cbs = this.disconnectCallbacks.get(hubName);
      if (cbs) {
        cbs.forEach((cb) => {
          try {
            cb(error);
          } catch (e) {
            console.error('[SignalR] disconnect callback error:', e);
          }
        });
      }
      if (error) {
        console.error(
          `WebSocket error for '${hubName}':`,
          error.message || error
        );
      }
    });

    hubConnection.onreconnecting((error) => {
      this.connectionStateSubject.next({
        hub: hubName,
        state: HubConnectionState.Reconnecting,
      });
      console.warn(`Reconnecting to '${hubName}'...`, error);
    });

    hubConnection.onreconnected((connectionId) => {
      this.connectionStateSubject.next({
        hub: hubName,
        state: HubConnectionState.Connected,
      });
      const cbs = this.reconnectCallbacks.get(hubName);
      if (cbs) {
        cbs.forEach((cb) => {
          try {
            cb(connectionId);
          } catch (e) {
            console.error('[SignalR] reconnect callback error:', e);
          }
        });
      }
    });

    try {
      const startPromise = hubConnection.start().then(() => {
        this.connectionStateSubject.next({
          hub: hubName,
          state: HubConnectionState.Connected,
        });
      });
      this.hubStartPromises.set(hubName, startPromise);
      await startPromise;
    } catch (err) {
      this.connectionStateSubject.next({
        hub: hubName,
        state: HubConnectionState.Disconnected,
      });
      console.error(`Connection to '${hubName}' hub failed:`, err);

      try {
        await this.authService.getValidAccessToken();
      } catch {}

      setTimeout(
        () => this.connect(hubName, url, queryParams, consumerId),
        5000
      );
    } finally {
      this.hubStartPromises.delete(hubName);
    }
  }

  /**
   * Listens for a specific event on a specified hub as an Observable.
   * @param hubName The name of the hub to listen on.
   * @param procedureName The name of the method to listen for (e.g., 'ReceiveNotification').
   * @returns An Observable that emits the received data.
   */
  public on<T>(hubName: string, procedureName: string): Observable<T> {
    const hubConnection = this.hubConnections.get(hubName);
    if (!hubConnection) {
      console.warn(`No SignalR connection found for hub '${hubName}'.`);
      return new Observable<T>((observer) =>
        observer.error('Hub connection not established.')
      );
    }

    return new Observable<T>((observer) => {
      const handler = (payload: T) => {
        observer.next(payload);
      };

      hubConnection.on(procedureName, handler);

      return () => {
        hubConnection.off(procedureName, handler);
      };
    }).pipe(takeUntil(this.destroy$));
  }

  /**
   * Invokes a server-side method on a specified hub.
   * @param hubName The name of the hub to invoke the method on.
   * @param methodName The name of the server-side method.
   * @param args The arguments to be sent to the method.
   */
  public async invoke(
    hubName: string,
    methodName: string,
    ...args: any[]
  ): Promise<any> {
    const hubConnection = this.hubConnections.get(hubName);
    if (hubConnection?.state !== HubConnectionState.Connected) {
      console.warn(
        `SignalR connection to '${hubName}' is not established. Cannot invoke methods.`
      );
      return;
    }

    try {
      return await hubConnection.invoke(methodName, ...args);
    } catch (err) {
      console.error(`Error invoking '${methodName}' on '${hubName}':`, err);
    }
  }

  /**
   * Terminates the connection to a specified hub.
   * @param hubName The name of the hub whose connection will be terminated.
   * @param force Whether to forcefully disconnect regardless of reference count.
   * @param consumerId The unique identifier for the consumer.
   */
  public async disconnect(
    hubName: string,
    force: boolean = false,
    consumerId: string = 'default'
  ): Promise<void> {
    const hubConnection = this.hubConnections.get(hubName);
    if (!hubConnection) return;

    const consumers = this.hubConsumers.get(hubName);
    if (!force && consumers) {
      consumers.delete(consumerId);
      const remaining = consumers.size;
      if (remaining > 0) {
        return;
      }
    }

    this.hubConsumers.delete(hubName);
    try {
      await hubConnection.stop();
      this.connectionStateSubject.next({
        hub: hubName,
        state: HubConnectionState.Disconnected,
      });
      this.hubConnections.delete(hubName);
      const callbacks = this.disconnectCallbacks.get(hubName);
      callbacks?.forEach((cb) => cb());
    } catch (err) {
      console.error(`Error stopping connection to '${hubName}':`, err);
    }
  }

  /**
   * Disconnects all hub connections.
   * @param force Whether to forcefully disconnect all connections.
   */
  public async disconnectAll(force: boolean = false): Promise<void> {
    const hubNames = Array.from(this.hubConnections.keys());
    for (const hubName of hubNames) {
      await this.disconnect(hubName, true);
    }
    if (force) {
      this.hubConsumers.clear();
    }
  }

  /**
   * Gets the current state of a specified hub.
   * @param hubName The name of the hub.
   * @returns The current state of the hub connection.
   */
  public getHubState(hubName: string): HubConnectionState | undefined {
    return this.hubConnections.get(hubName)?.state;
  }

  /**
   * Registers a callback to be executed when a hub disconnects.
   * @param hubName The name of the hub.
   * @param cb The callback function to execute on disconnect.
   */
  public onDisconnected(hubName: string, cb: Function) {
    const list = this.disconnectCallbacks.get(hubName) ?? [];
    list.push(cb);
    this.disconnectCallbacks.set(hubName, list);
  }

  /**
   * Registers a callback to be executed when a hub reconnects.
   * @param hubName The name of the hub.
   * @param cb The callback function to execute on reconnect.
   */
  public onReconnected(hubName: string, cb: Function) {
    const list = this.reconnectCallbacks.get(hubName) ?? [];
    list.push(cb);
    this.reconnectCallbacks.set(hubName, list);
  }

  /**
   * Terminates all hub connections when the application is shut down.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.hubConnections.forEach((hub) => {
      if (hub.state === HubConnectionState.Connected) {
        hub.stop();
      }
    });
    this.hubConsumers.clear();
  }
}
